import React, { useEffect, useState, useCallback } from 'react';
import './UserManagementTable.css';
import ManagerSidebar from '../../../components/Sidebar/ManagerSidebar';

import {
  FaUserPlus,
  FaUserEdit,
  FaTrash,
  FaUserShield,
  FaEnvelope,
  FaKey,
  FaIdBadge,
  FaTimes,
  FaSearch,
  FaCircle
} from 'react-icons/fa';

const UserManagementTable = () => {
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'Tenant'
  });

  const [editingId, setEditingId] = useState(null);

  const [status, setStatus] = useState({
    type: '',
    msg: ''
  });

  const [showForm, setShowForm] = useState(false);

  const API_BASE =
    'https://ikaze-thouse-backend.vercel.app/api/v1/auth';

  // ================= FETCH USERS =================
  const fetchUsers = useCallback(async () => {
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/users`);
      const data = await res.json();

      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      showAlert('error', 'Failed to fetch users.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ================= ALERT =================
  const showAlert = (type, msg) => {
    setStatus({ type, msg });

    setTimeout(() => {
      setStatus({ type: '', msg: '' });
    }, 4000);
  };

  // ================= INPUT CHANGE =================
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const url = editingId
      ? `${API_BASE}/users/${editingId}`
      : `${API_BASE}/signup`;

    try {
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await res.json();

      if (data.success) {
        showAlert(
          'success',
          editingId
            ? 'User updated successfully.'
            : 'User registered successfully.'
        );

        resetForm();
        fetchUsers();
      } else {
        showAlert('error', data.message || 'Operation failed.');
      }
    } catch (error) {
      showAlert('error', 'Server connection failed.');
    }
  };

  // ================= RESET =================
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'Tenant'
    });

    setEditingId(null);
    setShowForm(false);
  };

  // ================= EDIT =================
  const handleEdit = (user) => {
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role
    });

    setEditingId(user.id);
    setShowForm(true);

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // ================= DELETE =================
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this user?'
    );

    if (!confirmDelete) return;

    try {
      const res = await fetch(`${API_BASE}/users/${id}`, {
        method: 'DELETE'
      });

      const data = await res.json();

      if (data.success) {
        showAlert('success', 'User deleted successfully.');
        fetchUsers();
      } else {
        showAlert('error', data.message);
      }
    } catch (error) {
      showAlert('error', 'Delete failed.');
    }
  };

  // ================= FILTER USERS =================
  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-container">
      <ManagerSidebar />

      <main className="main-content">
        {/* HEADER */}
        <header className="content-header">
          <div className="header-text">
            <h1>User Directory</h1>
            <p>Manage users and their access roles.</p>
          </div>

          <button
            className={`btn-primary ${showForm ? 'btn-cancel' : ''}`}
            onClick={() =>
              showForm ? resetForm() : setShowForm(true)
            }
          >
            {showForm ? (
              <>
                <FaTimes /> Close
              </>
            ) : (
              <>
                <FaUserPlus /> Add User
              </>
            )}
          </button>
        </header>

        {/* ALERT */}
        {status.msg && (
          <div className={`alert-banner ${status.type}`}>
            {status.msg}
          </div>
        )}

        {/* FORM */}
        {showForm && (
          <section className="form-card animate-in">
            <h3>
              {editingId
                ? 'Update User Information'
                : 'Register New User'}
            </h3>

            <form onSubmit={handleSubmit} className="grid-form">
              {/* NAME */}
              <div className="input-group">
                <label>
                  <FaIdBadge /> Full Name
                </label>

                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required
                />
              </div>

              {/* EMAIL */}
              <div className="input-group">
                <label>
                  <FaEnvelope /> Email Address
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  required
                />
              </div>

              {/* PASSWORD */}
              {!editingId && (
                <div className="input-group">
                  <label>
                    <FaKey /> Password
                  </label>

                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    required
                  />
                </div>
              )}

              {/* ROLE DROPDOWN */}
              <div className="input-group">
                <label>
                  <FaUserShield /> Select Role
                </label>

                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="role-dropdown"
                  required
                >
                  <option value="Tenant">Tenant</option>
                  <option value="Manager">Manager</option>
                  <option value="Booking">Booking</option>
                </select>
              </div>

              {/* SUBMIT */}
              <div className="form-actions">
                <button type="submit" className="btn-submit">
                  {editingId
                    ? 'Save Changes'
                    : 'Register User'}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* SEARCH */}
        <section className="table-controls">
          <div className="search-bar">
            <FaSearch />

            <input
              type="text"
              placeholder="Search by name or email..."
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="table-stats">
            Showing <strong>{filteredUsers.length}</strong> users
          </div>
        </section>

        {/* TABLE */}
        <div className="table-wrapper">
          <table className="modern-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User Details</th>
                <th>Role</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="loader-cell">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td className="text-muted">
                      #{user.id}
                    </td>

                    <td>
                      <div className="user-info">
                        <div className="avatar">
                          {user.name.charAt(0)}
                        </div>

                        <div>
                          <div className="user-name">
                            {user.name}
                          </div>

                          <div className="user-email">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span
                        className={`badge ${user.role.toLowerCase()}`}
                      >
                        {user.role}
                      </span>
                    </td>

                    <td>
                      <span className="status-indicator">
                        <FaCircle /> Active
                      </span>
                    </td>

                    <td className="text-right action-btns">
                      <button
                        className="btn-icon edit"
                        title="Edit"
                        onClick={() => handleEdit(user)}
                      >
                        <FaUserEdit />
                      </button>

                      <button
                        className="btn-icon delete"
                        title="Delete"
                        onClick={() => handleDelete(user.id)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="loader-cell">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default UserManagementTable;